/* eslint-disable sonarjs/cognitive-complexity */
import {
  DeliveryType,
  ErrorCode,
  type PaymentMethod,
} from '@ahm/api-wrappers-core';
import { useAuth } from '@ahm/react-auth';
import { toast } from '@ahm/react-ui';
import { openContextModal } from '@mantine/modals';
import { useRouter } from 'next/navigation';
import { useCallback, useMemo, useState } from 'react';
import { useCreateOrderPartner } from '@/api/order';
import { ModalName, showModal } from '@/components/modals';
import { publicEnv } from '@/config/public-env.config.mjs';
import { ROUTES } from '@/config/router';

import { useCreateIntercityOrder } from '@/features/booking/api/intercity';
import { useCreateNewOrder } from '@/features/booking/api/order/use-create-order';
import { useOptimizedRouteHook } from '@/hooks';
import { usePaymentMethodHook } from '@/hooks/use-payment-method';
import {
  INVALID_PROMOTION,
  INVALID_PROMOTION_CREATE,
  SESSION_STORAGE_ORDER_ID_LATEST_CREATED,
} from '@/lib/constants';
import {
  checkPartnerFromToken,
  getServiceType,
  isServiceRestrictedByBan,
  objectToQuery,
  payWithEWallet,
} from '@/lib/utils';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

const PACKAGE_DETAIL_ERROR = 'package-detail';

export function useCreateOrder(
  createRequestBody,
  pathname,
  selectedPaymentMethod,
  paramPromo,
  isMobile
) {
  const { isTruckService, isBikeService, isHMService } = useMemo(
    () => getServiceType(pathname),
    [pathname]
  );
  const router = useRouter();
  const { session } = useAuth();
  const token = session?.token;
  const [createdOrderId, setCreatedOrderId] = useState('');

  const {
    setCreateOrderError,
    estimatedData,
    truckSelectedGroup,
    selectedService,
  } = useBookingStore((state) => state);

  const { showNotEnoughBalanceModal, needLinkMethod } = usePaymentMethodHook();
  const { estimatedShortestRoute } = useOptimizedRouteHook();

  const generateRoute = (orderId: string, paymentMethod: PaymentMethod) => {
    const isProd = publicEnv.NEXT_PUBLIC_APP_ENV === 'production';
    const isPayByEWallet = payWithEWallet(paymentMethod?.method_type || '');
    const baseRoute: string = isPayByEWallet
      ? isProd
        ? 'payment'
        : 'pay'
      : 'record';
    const currentUrl = new URL(window.location.href);
    const existingParams = Object.fromEntries(
      Array.from(currentUrl.searchParams.entries()).filter(
        ([key]) => key !== 'promo_code'
      )
    );

    const queryParams = {
      ...existingParams,
      ...(isPayByEWallet
        ? {
            ...(!needLinkMethod(paymentMethod) &&
            !existingParams.isPayOrderWithAutoDebit
              ? { isPayOrderWithAutoDebit: 'true' }
              : {}),
            ...(existingParams.inAppRedirect ? {} : { inAppRedirect: 'true' }),
          }
        : {}),
    };

    const recordPathV3 = `/${baseRoute}/${orderId}?${objectToQuery(queryParams)}`;
    const recordPathV1 = `${process.env.NEXT_PUBLIC_APP_V1_URI}/${baseRoute}/${orderId}?${objectToQuery(queryParams)}`;

    return { pathname, recordPathV1, recordPathV3, isProd };
  };

  const isPartner = token ? checkPartnerFromToken(token) : false;

  const { mutateAsync: onCreateNewOrder, isPending: isNewOrderPending } =
    useCreateNewOrder({
      onSuccess: handleCreateSuccess,
      onError: handleCreateError,
    });

  const { mutateAsync: onCreateIntercityOrder, isPending: isIntercityPending } =
    useCreateIntercityOrder({
      onSuccess: handleCreateSuccess,
      onError: handleCreateError,
    });

  const {
    mutateAsync: onCreateOrderPartner,
    isPending: isOrderPartnerPending,
  } = useCreateOrderPartner({
    onSuccess: handleCreateSuccess,
    onError: handleCreateError,
  });

  const handleCreateOrder = useCallback(async () => {
    if (
      createdOrderId ||
      isNewOrderPending ||
      isIntercityPending ||
      isOrderPartnerPending
    ) {
      toast.error({
        title: 'Đơn hàng của bạn đang được tạo',
        message:
          'Vui lòng không tạo đơn hàng mới trong khi đơn hàng này đang được xử lý.',
        withIcon: true,
      });
      return;
    }
    if (!createRequestBody) return;

    const estimatedShotestResult = await estimatedShortestRoute();

    if (pathname === ROUTES.nationwide) {
      await onCreateIntercityOrder(createRequestBody);
      return;
    }

    const shouldOptimizeRoute =
      estimatedData?.data &&
      estimatedShotestResult?.data &&
      estimatedShotestResult?.data.distance < estimatedData.data?.distance;

    const createOrder = isPartner ? onCreateOrderPartner : onCreateNewOrder;

    if (shouldOptimizeRoute) {
      openContextModal({
        modal: ModalName.OptimizeRoute,
        modalId: ModalName.OptimizeRoute,
        innerProps: {
          estimatedShotestResult,
          onCancel: async () => await createOrder(createRequestBody),
        },
        centered: true,
        fullScreen: isMobile,
        withCloseButton: false,
        styles: {
          body: {
            position: 'relative',
            padding: 0,
          },
        },
      });
      return;
    }
    // Validate package details before creating order
    if (!createRequestBody?.item_descriptions?.length && !isHMService) {
      setCreateOrderError(PACKAGE_DETAIL_ERROR);
      return;
    }

    if (
      (isTruckService || isHMService) &&
      isServiceRestrictedByBan(createRequestBody.selectedService)
    ) {
      toast.error({
        title: 'Dịch vụ bạn chọn nằm trong giờ cấm tải.',
        message: 'Xin vui lòng chọn dịch vụ khác.',
        withIcon: true,
      });
      return;
    }
    if (isBikeService && !createRequestBody?.package_weight) {
      setCreateOrderError(PACKAGE_DETAIL_ERROR);
      return;
    }
    await createOrder(createRequestBody);
  }, [
    createRequestBody,
    pathname,
    onCreateNewOrder,
    onCreateIntercityOrder,
    onCreateOrderPartner,
    estimatedData,
    estimatedShortestRoute,
    isPartner,
    isBikeService,
    isTruckService,
    truckSelectedGroup,
    setCreateOrderError,
    isMobile,
    createdOrderId,
    isNewOrderPending,
    isIntercityPending,
    isOrderPartnerPending,
  ]);

  function handleCreateSuccess(data) {
    if (
      selectedService?.delivery_type === DeliveryType.SameDay &&
      !selectedService._id.includes('INDAY')
    ) {
      sessionStorage.setItem(
        SESSION_STORAGE_ORDER_ID_LATEST_CREATED,
        data.order_id || ''
      );
    }

    setCreatedOrderId(data.order_id);
    const route = generateRoute(data.order_id, selectedPaymentMethod);
    if (route?.isProd) {
      window.location.replace(route?.recordPathV1);
    } else {
      void router.push(route?.recordPathV3);
    }
  }

  function handleCreateError(error) {
    if (error.response?.data) {
      const { code, title, description } = error.response.data;
      if (code === ErrorCode.NotEnoughCredit) {
        showNotEnoughBalanceModal();
        return;
      }

      if (code === ErrorCode.InvalidServiceHour) {
        openContextModal({
          modal: ModalName.InvalidServiceHour,
          modalId: ModalName.InvalidServiceHour,
          centered: true,
          fullScreen: false,
          withCloseButton: false,
          styles: {
            body: {
              position: 'relative',
              padding: '16px',
            },
          },
          innerProps: undefined,
        });
        return;
      }

      if (code === INVALID_PROMOTION_CREATE || code === INVALID_PROMOTION) {
        showModal(
          ModalName.CreateOrderWithoutPromo,
          { promo_id: paramPromo, createRequestBody },
          {
            mobileType: 'drawer',
            styles: {
              content: {
                backgroundColor: 'white',
              },
            },
          }
        );
      } else {
        title
          ? toast.error({
              title,
              message: description,
              withIcon: true,
            })
          : null;
      }
    }
  }

  return handleCreateOrder;
}
